import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../store";
import { RRAPitchbookCompany } from "../types/pitchbook";
import { VendorType } from "../common/constants";
import { ICompanyProfile } from "../../../types/pub";

type ICompanySlideoutValues = {
  company: RRAPitchbookCompany;
  hideSaveCompanyAction?: boolean;
};

export type CompanyProfile = ICompanySlideoutValues[] | undefined;

export interface CompanyBuilderAppState {
  companyProfile: CompanyProfile;
  // Company details state
  isCompanyDetailsOpen: boolean;
  companyDetails: (RRAPitchbookCompany | ICompanyProfile)[] | null;
  vendor: VendorType;
  // companySearch related
  shortlists: string[];
}

const initialState: CompanyBuilderAppState = {
  companyProfile: undefined,
  // Company details initial state
  isCompanyDetailsOpen: false,
  companyDetails: null,
  vendor: VendorType.RRA_PITCHBOOK,
  // companySearch related
  shortlists: [],
};

export const companyBuilderAppSlice = createSlice({
  name: "companyBuilderAppState",
  initialState,
  reducers: {
    addCompanyProfile: (
      state,
      action: PayloadAction<{
        company: RRAPitchbookCompany;
        entityType?: 'Company' | 'Investor';
        hideSaveCompanyAction?: boolean;
      }>
    ) => {
      if (state.companyProfile === undefined) {
        state.companyProfile = [{
          company: action.payload.company,
          hideSaveCompanyAction: action.payload.hideSaveCompanyAction,
        }];
      } else if (
        Array.isArray(state.companyProfile) &&
        state.companyProfile.at(-1)?.company.ref !==
        action.payload?.company.ref
      ) {
        // check if last elem in array is not the same as the incoming elem
        state.companyProfile.push(action.payload);
      }
    },
    removeCompanyProfile: (state) => {
      state.companyProfile?.pop();
    },
    closeAllCompanyProfile: (state) => {
      state.companyProfile = undefined;
    },
    // Company details actions
    openCompanyDetails: (state, action: PayloadAction<{ companyDetails: RRAPitchbookCompany | ICompanyProfile, vendor: VendorType }>) => {
      state.isCompanyDetailsOpen = true;
      state.companyDetails = [action.payload.companyDetails];
      // if action.payload is ICompanyProfile, then set the vendor to the vendor type as crunchbase
      state.vendor = action.payload.vendor as VendorType;
    },
    closeCompanyDetails: (state) => {
      state.isCompanyDetailsOpen = false;
      state.companyProfile = undefined;
    },
    setCompanyDetails: (state, action: PayloadAction<RRAPitchbookCompany | null>) => {
      state.companyDetails = action.payload ? [action.payload] : null;
    },
    // companySearch related
    setShortlists: (state, action: PayloadAction<string[]>) => {
      state.shortlists = action.payload;
    },
    addShortlists: (state, action: PayloadAction<string[]>) => {
      state.shortlists = Array.from(new Set([...state.shortlists, ...action.payload]));
    }
  },
});

// Accessors
export const selectCompanyProfileSlideOut = (state: RootState) =>
  state.companyBuilderAppState.companyProfile?.at(-1);

export const selectCompanyDetails = (state: RootState) => {
  const companyProfile = state.companyBuilderAppState.companyProfile?.at(-1);
  const company = companyProfile?.company;
  return {
    isOpen: state.companyBuilderAppState.isCompanyDetailsOpen,
    company,
    companyName: company?.company_name || "Unknown Company",
    hideSaveCompanyAction: Boolean(companyProfile?.hideSaveCompanyAction),
    logoUrl: company?.logos?.filter((logo) => logo.source === 'linkedin').map((logo) => logo.ref)[0] ?? undefined,
    vendor: state.companyBuilderAppState.vendor,
  };
};

export const {
  addCompanyProfile,
  removeCompanyProfile,
  closeAllCompanyProfile,
  openCompanyDetails,
  closeCompanyDetails,
  setCompanyDetails,
  setShortlists,
  addShortlists,
} = companyBuilderAppSlice.actions;

export default companyBuilderAppSlice.reducer;
